import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { EmergeLockup } from '@/components/EmergeLockup';
import {
  Button,
  ConfirmDialog,
  Field,
  Input,
  Panel,
  PanelBody,
  PanelHeader,
  Select,
  Skeleton,
  Textarea,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { ExternalTextLink } from '@/components/ui/TextLink';
import { ApiError, api } from '@/lib/api';
import { formatDateTime, orDash } from '@/lib/format';
import { BILLING_NAME_LABEL, secondLineBillingName } from '@/lib/company-names';
import { useAuth } from '@/lib/use-auth';
import { LINE_AMOUNT_OPTIONS } from '@/lib/xero-tax-rates';
import type {
  ApprovalCurrencyOption,
  ApprovalOptionList,
  CompanyApprovalApprover,
  CompanyApprovalCompany,
  CompanyApprovalDecisionIdentity,
  CompanyApprovalRequest,
  XeroLineAmountType,
} from '@/lib/types';

/**
 * The add-a-school approval page — `/company-approvals/:token`, aliased at
 * `/approve-company?token=…`.
 *
 * ## Who is reading this
 *
 * 🔴 **A SIGNED-IN MEMBER OF THE APPROVERS GROUP — this page went behind
 * `RequireAuth` on 2026-09-11, on the owner's instruction.** *SUPERSEDED, and
 * it was the premise of this whole file: "The PUBLIC add-a-school approval page
 * … Somebody who clicked a link in an email, is **not signed in**, and may
 * never have opened EMERGE. That single fact drives most of what follows."*
 *
 * **Measured the same day: the approvers group holds four people and all four
 * are EMERGE users** (two `super_admin`, two `admin`). The reader that premise
 * described — an approver with no account — does not exist. What the gate buys
 * is the thing migration 109 knowingly gave up: the approver used to be
 * **STATED**, picked from a dropdown, because one shared link cannot identify
 * its holder. The session identifies them now.
 *
 * ⚠️ **NOT EVERYTHING DOWNSTREAM OF THE OLD PREMISE MOVED, and the surviving
 * halves are marked where they sit.** In particular the three api methods are
 * still `publicRequest` and must stay that way, and the page still renders its
 * own `<main>` with no `AppShell` — that one for a new reason:
 *
 * - It renders its own `<main>` and no `AppShell`, exactly like `LoginPage`.
 *   ⚠️ *SUPERSEDED, the reason: "because there is no session to sign out of."*
 *   There is one now. It stays chrome-less because this is **one decision
 *   arrived at from an email**, not a stop inside the CRM — framing a single
 *   yes/no in the whole app would bury it — and because `Shell` would give the
 *   document a second `<main>`.
 * - **Nothing here may call `useAuth()`.** There is no user, and a hook that
 *   expects one turns "not signed in" into a crash on the one page whose whole
 *   audience is not signed in.
 * - **No authenticated endpoint is called from this page, ever.** See
 *   {@link XERO_LISTS_UNAVAILABLE} — this is the constraint that shapes the form.
 *
 * ## The three api methods are PUBLIC, and STAY public
 *
 * `getCompanyApproval` / `approveCompany` / `rejectCompany` go through
 * `publicRequest`: no bearer token, no part in the 401-refresh retry.
 *
 * ⚠️ **THE GATE DID NOT RETIRE THIS, and "the page is authenticated now, so use
 * the authed client" is the change to refuse.** The half that moved is the
 * colleague: it is no longer a stranger's browser, because the signed-in person
 * IS the approver. The half that did not is the **401-refresh retry** — a dead
 * or spent approval token must never provoke a refresh, because a refresh token
 * is single-use and the server treats a replay as theft by revoking every
 * device. Routing these three through the authed client would trade a dead link
 * for a mass sign-out.
 *
 * ⛔ **AND THE SERVER STILL TAKES A SUBMITTED `statedApproverId`.** The gate is
 * a CLIENT-SIDE convention over a public endpoint: it stops a person choosing
 * somebody else's name in this UI, it does not stop the request. **Never
 * describe it as access control.** The backend half — read the approver from
 * the token and ignore the body — is an open ask; when it lands, this section
 * and `statedIdentity` are what change.
 *
 * ## ⚠️ The pickers come from the `GET`, and nowhere else
 *
 * The tax code, the letterhead and the currency are **tenant-specific Xero
 * reference data**. This page cannot read any of it: `GET /xero/tax-rates`,
 * `/xero/branding-themes` and `/currencies` all need a bearer token, and on a
 * browser where a colleague happens to be signed in, calling one would spend
 * that colleague's rate-limited Xero budget on a page they are not looking at.
 *
 * So the public `GET` carries all three, already filtered and ordered
 * server-side, behind a ten-minute cache with a thirty-second failure cooldown.
 * {@link ApprovalOptionList} is a **discriminated union and never a bare array**:
 * `{status:'unavailable'}` and `{status:'ok', options:[]}` are different facts,
 * and a `?? []` anywhere on this page would delete the difference.
 *
 * ⚠️ *SUPERSEDED 2026-09-05: "See {@link listNotice}."* That function is gone
 * with the owner's stricter helper-text ruling, so the distinction is **no
 * longer narrated to the reader** — but it is still REAL and still decides
 * behaviour through {@link hasOptions}, which disables the currency and theme
 * pickers and swaps the tax-type control for a text input. **Do not now write
 * `?? []` on the grounds that nothing reads the difference.**
 *
 * ## ⚠️ Three things this page must not do
 *
 * - **Do not re-filter or re-sort the lists.** Tax rates are already narrowed to
 *   `canApplyToRevenue === true` and ACTIVE; themes already carry Xero's order
 *   with a null `sortOrder` sorted LAST; currencies are already intersected with
 *   the organisation's enabled codes. Every one of those is a decision made
 *   against data this page cannot see.
 * - **Do not offer a value the `GET` did not supply.** A branding theme is
 *   format-checked only — a wrong GUID is *stored* and forwarded to Xero
 *   unexamined, not refused — so an id typed from memory fails silently, months
 *   later, on somebody's document.
 * - **Do not send a tax RATE.** The code alone; the server derives the
 *   percentage from Xero's own `EffectiveRate`.
 *
 * ## ⚠️ One link, several approvers — and the name is the SESSION now
 *
 * The owner ruled on 2026-08-30 that the email goes out **once, to all
 * approvers in `To`, carrying ONE link** rather than one link each. **That is
 * unchanged**; what changed is the answer to "who is holding it".
 *
 * 🔴 *SUPERSEDED 2026-09-11: "A shared link cannot name its holder, so the page
 * asks."* It no longer asks — the page is gated and the session names the
 * holder, matched against the server's own approvers list. See
 * {@link ApproverField}, now a read-only block, and {@link NotAnApprover} for
 * the state that creates: a shared link genuinely does get forwarded, so
 * "signed in, but not an approver" is an ordinary arrival rather than an edge.
 *
 * ✅ **The contract is settled and the key is `statedApproverId`**, REQUIRED on
 * both approve and reject, confirmed from `company-approvals.schema.ts` on
 * 2026-08-30. This page previously guessed `decidedByUserId` and made both the
 * list and the key optional, so it could ship ahead of the server. **That hedge
 * is gone, and it had to go**: an absent `statedApproverId` is a `422` naming a
 * field the page never rendered, so the "older server" fallback — no identity
 * step, both buttons live — was the one arrangement guaranteed to fail. There is
 * no version of this page that works against both servers; the backend deploys
 * first.
 *
 * ⚠️ **An EMPTY approvers list means nobody can decide, not "do not ask".** See
 * {@link NoApprovers}, which is the screen that used to be a live form.
 *
 * ⚠️ **THE COPY PROHIBITION IS LIFTED ON THE CLIENT SIDE AND STILL BINDS ON THE
 * SERVER SIDE — read this before writing any sentence about who decided.**
 * *SUPERSEDED, in its unqualified form: "It is a STATED identity, never a
 * verified one … Nothing on this page may say signed in, verified,
 * authenticated or confirmed, and nothing may imply the system recognises the
 * reader. Anyone holding the link can select any name."*
 *
 * Nobody can select any name any more: there is no control, and the id sent is
 * the session's, matched against the server's list. So **this page may now say
 * "Signed in as" and "Deciding as"**, and both are true of the reader in front
 * of it.
 *
 * ⛔ **What it may still NOT say is that the RECORD is verified.** The endpoint
 * is public and takes `statedApproverId` from the body, so a caller outside
 * this UI can still put any group member's id on a decision. The stored
 * attribution is exactly as strong as it was — see
 * `CompanyApprovalDecisionIdentity`, whose `attribution` field exists to keep
 * that caveat attached to the name wherever it is printed. **Never word a
 * DECIDED-BY line as verified**, here or on the company record, until the
 * backend takes the actor from the token.
 *
 * The old paragraph's last clause is untouched and still right: **do not
 * editorialise about attribution either way** — a page that apologises for its
 * own record teaches people to distrust it.
 *
 * ⚠️ **The consequence for the terminal screens: a `410` is now the ORDINARY
 * outcome of a second reader clicking.** One shared link is spent by whoever
 * acts first, so everyone else — including the decider reloading — arrives at
 * {@link GONE_TITLE}. It is toned as an ordinary event for that reason, and it
 * still refuses to say WHICH of the four causes applies; see that constant.
 */

/**
 * The screens this page can be on. A discriminated union rather than a bag of
 * booleans because several of these are **mutually exclusive by construction**
 * and the bug they prevent is showing a form beside a message saying the form
 * cannot work.
 *
 * `loading`, `unavailable` and `gone` are deliberately three states and not one
 * "not ready": they want three different sentences and three different tones,
 * and collapsing any two tells the reader something untrue about whether to try
 * again.
 */
type Phase =
  | { kind: 'loading' }
  /** The request did not complete: offline, DNS, a 5xx. Retrying is meaningful. */
  | { kind: 'unavailable'; message: string }
  /** `410` — spent, expired, or already decided. One screen; see {@link GONE_TITLE}. */
  | { kind: 'gone' }
  | { kind: 'ready'; view: CompanyApprovalRequest }
  /**
   * Already settled — read from `status`, not inferred. Covers both a decision
   * another approver made before this page loaded and the one just submitted
   * here, because the reader needs the same thing in both cases: the outcome.
   */
  | {
      kind: 'settled';
      outcome: 'approved' | 'rejected';
      companyName: string;
      /**
       * The Billing name (the Xero Contact Name), shown under the Company name
       * heading (owner 2026-10-27). `undefined` = an older API: show nothing.
       */
      billingName: string | null | undefined;
      byMe: boolean;
      /**
       * The name this page SAID the decision was made by, when it made it.
       *
       * `null` on every screen this page did not write — a decision another
       * holder of the link made is not one this page can attribute, and the
       * server's `decidedBy` is deliberately not read here in its place. It
       * names the account the row was filed under, which under a stated identity
       * is the same claim arriving from further away, and printing it would make
       * an unverified selection look like a server-side fact.
       */
      statedName: string | null;
    };

/**
 * ⚠️ **ONE message for unknown, expired, spent and superseded.**
 *
 * The server answers `410 COMPANY_APPROVAL_LINK_INVALID` for all four and
 * deliberately does not say which — a differently-worded refusal for a
 * well-formed token would tell somebody grinding tokens which of their guesses
 * were worth making. Inventing a distinction the API refuses to make would
 * therefore be both an unverifiable claim and a small oracle; the likeliest
 * reading ("expired") is also the one that sends a person chasing a new link for
 * a request a colleague already decided.
 *
 * ⚠️ **Under a shared link this is the ORDINARY ending, not the unhappy one.**
 * One link is spent by whoever acts first, so every other approver — and the
 * decider on a reload — lands here having done nothing wrong. It is toned
 * **neutral, not danger**, and the possibilities are ordered with "already used
 * to decide it" first: that is the common case now, and ordering by likelihood
 * asserts nothing that naming one cause would. A red failure box here reads as
 * "your approval did not work", which is false and is the sort of thing somebody
 * acts on twice.
 *
 * Note this is the *fallback*: where the server answers `200` with a settled
 * `status`, the reader gets the real outcome instead. See {@link Phase}.
 */
const GONE_TITLE = 'This approval link is no longer active';

/*
 * `listNotice()` is GONE — owner's stricter helper-text ruling, 2026-09-05. It
 * returned one of three sentences about an option list that could not be read,
 * was not connected, or came back empty. None is a server refusal, so none
 * survives the bar. The three states still decide BEHAVIOUR — `hasOptions()`
 * below still disables the control and still drives the text fallback on tax
 * type — they are simply no longer narrated.
 */

/** Whether a list yielded anything to choose from. */
function hasOptions(list: ApprovalOptionList<unknown>): boolean {
  return list.status === 'ok' && list.options.length > 0;
}

/**
 * ⚠️ **"Similar", never "duplicate", and never a merge offer.**
 *
 * Xero's contacts — and therefore SEED's customers — are **school-level, not
 * university-level**. `Trustees of Boston University` and `Boston University -
 * Questrom School of Business` are two correct, separate customers with two
 * separate PO processes. A near-match is a reason to *look*, not a finding.
 *
 * Wording this as a duplicate warning would push an approver towards rejecting
 * a legitimate school, and the rejection would look entirely justified to
 * everyone downstream.
 */
const SIMILAR_NOT_DUPLICATE =
  'Two schools of one university are separate customers — a similar name is not a reason to reject.';

/**
 * The label for "leave this to Xero".
 *
 * ⚠️ **`null` is an ANSWER here, not an empty box**, and the contract makes
 * every sales-defaults key required while every value is nullable for exactly
 * that reason. It is the first option in Xero's own dropdowns. Offering it as
 * labelled text rather than a blank row is what keeps "the approver chose
 * organisation settings" distinguishable from "the approver skipped the field".
 */
const USE_ORG_SETTINGS = 'Use Xero organisation settings';

/** Who to contact, spelled once so the terminal screens cannot word it differently. */
const CONTACT_LINE = 'Contact the person who requested it.';

/* --- the country an institution typed, which resolved to nothing ---------- */

/**
 * ⚠️ **A STRING SOMEBODY TYPED, AND NEVER A COUNTRY.** Read this block before
 * changing a word of the three constants below.
 *
 * On the interest form's "Other" path a visitor types a country whatever happens.
 * Where `admin-app` could not resolve it, the company is created with **no
 * country of record** and the typed string is kept on the submission. It surfaces
 * on THIS page because the approver is the one person who can turn it into a real
 * country — and the company's own country stays blank until they do.
 *
 * ⛔ **NOTHING HERE RESOLVES IT.** No match against a country list, no "did you
 * mean", no seeding of any control from it, and above all **it is never rendered
 * in the {@link CompanyApprovalCompany.country} slot**. A guessed country reaches
 * the billing region, so it becomes a guessed currency and a guessed tax default
 * on a document a customer receives — and afterwards nothing distinguishes it
 * from a country a person chose. Migration 223 states the prohibition at length.
 *
 * ⛔ **AND IT IS NOT A NAG.** It gates nothing, blocks nothing, and carries no
 * instruction to go and fix it. It explains a blank the reader can already see.
 * That is why it survives the no-helper-text rule and why widening it into advice
 * would not: *a fact the reader cannot otherwise see* is one of the four kinds
 * that stay, and *what you should do next* is not one of them.
 *
 * ⚠️ **ONE WORDING, and this is the only place it exists.** If a second surface
 * ever shows this field, it imports these constants. A page saying "typed" beside
 * one saying "unresolved" or "claimed" reads as a difference in the data.
 */
const TYPED_COUNTRY_LABEL = 'Country typed on the interest form';

/**
 * ⚠️ **It explains the blank above it**, which is the whole of its job. Nothing
 * else on this page says why the Location line carries a city and no country, or
 * that a country was offered at all.
 */
const TYPED_COUNTRY_NOTE = 'Not matched to a country, so this company has none recorded.';

/**
 * The same fact where submissions disagree.
 *
 * ⚠️ **The tail is byte-identical to {@link TYPED_COUNTRY_NOTE} on purpose.** The
 * load-bearing clause — *so this company has none recorded* — must read the same
 * either way; only the sentence that reports the disagreement is added. Two
 * wordings of one fact is what this block forbids, and this is one fact plus a
 * second one, not the same fact worded twice.
 */
const TYPED_COUNTRY_DISAGREEMENT =
  'Different submissions typed different countries. None matched, so this company has none recorded.';

/**
 * The typed country names off the approval view, read through a runtime guard.
 *
 * ⚠️ **TAKES `unknown`, AND THAT IS NOT DEFENSIVENESS FOR ITS OWN SAKE.**
 * {@link CompanyApprovalRequest} does not declare this key — `types.ts` is owned
 * elsewhere and has not been widened — so the value genuinely arrives untyped.
 * Even once it is declared the honest declaration is optional, because this app
 * deploys separately from the API and can be NEWER than it: a key an older server
 * does not send reads back `undefined`. So the guard is the right reading either
 * way, and it is the same treatment the server gives the match snapshot —
 * validated at the boundary rather than asserted through it.
 *
 * ⚠️ **THE WIRE'S `null` AND `[]` ARE DELIBERATELY COLLAPSED HERE, AT THE RENDER,
 * AND NOWHERE ELSE.** The server distinguishes them: `[]` is *we looked and there
 * was nothing*, `null` is *we could not look*. The page treats both as **nothing
 * to show**, because there is no honest sentence to put in front of an approver
 * about a failed auxiliary read — the field is empty on nearly every company, so
 * a "we could not check" line would fire on almost every approval, about a fact
 * nobody can act on, and the reader's next step is identical either way. The
 * distinction survives on the wire and in the server's log, where the question
 * *did that read break* is actually answerable.
 *
 * ⛔ **So `[]` FROM THIS FUNCTION IS NOT EVIDENCE THE SERVER CHECKED.** Nothing
 * downstream may read it as one.
 */
function readTypedCountryNames(view: unknown): string[] {
  if (typeof view !== 'object' || view === null) return [];
  const typed = (view as Record<string, unknown>).typedCountryNames;
  if (!Array.isArray(typed)) return [];

  const kept: string[] = [];
  for (const entry of typed) {
    if (typeof entry !== 'string') continue;
    const trimmed = entry.trim();
    // De-duplicated server-side already; repeated here because this function's
    // output decides whether the disagreement wording fires, and claiming two
    // institutions disagreed when one string arrived twice is a worse error than
    // a redundant check.
    if (trimmed !== '' && !kept.includes(trimmed)) kept.push(trimmed);
  }
  return kept;
}

/* --- seeding the four controls from the billing region -------------------- */

/**
 * What the three seedable controls should START at for this company, or `null`
 * when nothing may be seeded.
 *
 * ## The defect this exists to close
 *
 * Before this, the four controls opened at `'EXCLUSIVE'` / `''` / `''` / `''` on
 * every company — so a UK school whose `united_kingdom` region default is fully
 * populated (`OUTPUT2`, GBP, `UK Schools GBP`) was shown three controls reading
 * {@link USE_ORG_SETTINGS}. **That is a false statement about a consequence**:
 * the approver was told the Xero organisation defaults would apply while the
 * REGIONAL ones were what would actually be used. The company data was never
 * wrong; the page simply had nothing to seed from.
 *
 * ## ⚠️ `''` is reached by TWO DIFFERENT ROUTES and they must not be collapsed
 *
 * A control left at `''` means "use the Xero organisation default" — a real
 * answer, and the one an approver may deliberately choose. A region default of
 * `null` means **no admin has set one**, which is a different fact that happens
 * to land on the same control state, because with nothing to apply the
 * organisation default is genuinely what happens.
 *
 * **They agree today and they are not the same claim.** Do not rewrite either as
 * the other, do not `?? 'NONE'` a null tax type (`NONE` is a real Xero code and
 * is what `rest_of_world` carries on purpose), and if a region ever gains a
 * "no default, and that is deliberate" state these two stop agreeing.
 *
 * ## `null` from this function means SEED NOTHING
 *
 * `billingRegion === null` is "we cannot say" — no country of record, or a
 * region this bundle does not know. Guessing there would put a tax code on a
 * document from a region nobody established. Returning `null` leaves all four
 * controls at their initial values and the approver answers for themselves.
 *
 * Line amounts is deliberately **not** in the result: the approval view carries
 * no region value for `xeroSalesLineAmountType`, so its `'EXCLUSIVE'` initial
 * state — the owner's ruling for a new company — is left exactly as it was.
 * There is nothing to seed it from, not a value that was forgotten.
 */
function seedFromBillingRegion(
  company: CompanyApprovalCompany,
  currencies: ApprovalOptionList<ApprovalCurrencyOption>,
): { taxType: string; currencyId: string; brandingThemeId: string } | null {
  if (company.billingRegion === null) return null;

  /*
   * ⚠️ An EXACT, case-sensitive match, and never `.toUpperCase()` on either
   * side. `billingCurrency` is a `^[A-Z]{3}$` code from the master and so is
   * every `option.code`; folding case would let a lower-cased value resolve to a
   * row here and then be refused on the write, where `?currency=usd` is a 422.
   *
   * A code with no matching option — an unreadable list, or a currency the
   * organisation has not enabled in Xero — yields `''`, which is the honest
   * answer: this page must not offer an id the write would refuse.
   */
  const currency =
    currencies.status === 'ok' && company.billingCurrency !== null
      ? currencies.options.find((option) => option.code === company.billingCurrency)
      : undefined;

  return {
    taxType: company.billingRegionXeroTaxType ?? '',
    currencyId: currency === undefined ? '' : String(currency.id),
    brandingThemeId: company.billingRegionXeroBrandingThemeId ?? '',
  };
}

/* --- the stated identity ------------------------------------------------- */

/**
 * The heading over the identity step.
 *
 * ⚠️ **"Who is deciding", not "Verify your identity" or "Sign in".** The page
 * asks a question it will believe the answer to; a heading naming a check the
 * system does not perform would be false about the mechanism and would also
 * make the step feel like a barrier rather than a signature.
 */
const IDENTITY_TITLE = 'Who is deciding';

/*
 * `SHARED_LINK_NOTE` is GONE — owner's stricter helper-text ruling, 2026-09-05.
 * It read: "Shared link — the decision is recorded against the name selected
 * here." It named a real invisible state (the token is shared, so the server
 * cannot tell who opened it) but it is not a server refusal, so it does not
 * survive the bar.
 *
 * ⚠️ What it explained is UNCHANGED in the mechanism: the approver is STATED,
 * never authenticated, and the server writes that stated name to its own column.
 * The reason Approve and Reject start inert is carried separately by
 * {@link IDENTITY_REQUIRED_REASON}, which stays — that one is a
 * why-this-is-disabled sentence and is an explicit keeper.
 */

/**
 * 🔴 **THE "SELECT YOUR NAME" ROW IS GONE WITH THE DROPDOWN — 2026-09-11.**
 * *SUPERSEDED: `const CHOOSE_NAME = 'Select your name'`, "the unchosen row …
 * the absence that keeps both buttons inert."* The page is behind
 * `RequireAuth`; the session says who this is, so there is no unchosen state to
 * name. **Its neighbour {@link USE_ORG_SETTINGS} is untouched and is still a
 * real answer** — the distinction that comment drew survives, it just no longer
 * has two dropdowns to draw it between.
 */

/** The sentence-case caption over a read-only value, as this page sets them. */
const IDENTITY_VALUE_LABEL = 'm-0 text-xs font-medium text-fg-muted';

/**
 * Why Approve and Reject are inert, rendered beside them.
 *
 * A bare `disabled` makes "you have not answered yet" and "this is broken"
 * identical, which on a page opened once from an email is the difference between
 * a decision and a support request. Same reasoning as `denialReason()` on the
 * signed-in surfaces, and it is the exception the house rule names explicitly.
 *
 * 🔴 **REWORDED 2026-09-11, because the old sentence named a control that no
 * longer exists.** *SUPERSEDED: "Select who is deciding to enable Approve and
 * Reject."* There is nothing to select now: the only way to reach this state is
 * to be signed in as somebody outside the approvers group, so the sentence has
 * to say that instead of asking for an answer the reader cannot give.
 * **Copy that names an absent control is worse than no copy** — it reads as the
 * app being broken and sends somebody hunting for a dropdown.
 */
const IDENTITY_REQUIRED_REASON =
  'Only the approvers group can decide this, and this account is not in it.';

/**
 * How one approver is labelled in the dropdown.
 *
 * ⚠️ **NO EMAIL FALLBACK, and this deliberately DIVERGES from
 * {@link personLabel} directly below it** — which does fall through to an email,
 * correctly, for the one requester named on this page.
 *
 * This list is different in kind: it is every member of the approvers group, on
 * a public page, behind a link that is now shared and forwardable. Printing
 * their addresses turns an approval screen into a directory of exactly the
 * people who can sign off a new customer — a phishing target list, permanent
 * once forwarded, and bought for nothing, because somebody recognises their own
 * name without being shown their own address. {@link CompanyApprovalApprover}
 * therefore does not declare an `email` at all, so this is structural rather
 * than a rule someone can forget.
 *
 * A blank `name` is labelled by POSITION instead. It identifies nobody, which is
 * honest — the alternatives are printing the address anyway or dropping the row,
 * and a dropped row is an approver who cannot pick themselves at all.
 */
function approverLabel(approver: CompanyApprovalApprover, index: number): string {
  return approver.name.trim() || `Approver ${index + 1}`;
}

/**
 * The identity fragment both decision bodies carry, built in ONE place.
 *
 * ⚠️ **This helper and {@link CompanyApprovalDecisionIdentity} are the only two
 * spellings of the key in this app**, which is what made correcting it a rename
 * rather than a hunt. It was `decidedByUserId`, a guess; the contract says
 * `statedApproverId`, and both verbs require it.
 *
 * ⚠️ **It takes an APPROVER, not the raw select value, and returns a REQUIRED
 * fragment.** The previous version took the `''`-able string and returned `{}`
 * for the unchosen case, so a caller could silently omit the key; against a
 * schema that requires it, that is a `422` naming a field the caller thought it
 * had handled. Taking a resolved approver means the only way to call it is to
 * have one, and `tsc` enforces the rest at the two call sites.
 */
function statedIdentity(approver: CompanyApprovalApprover): CompanyApprovalDecisionIdentity {
  return { statedApproverId: approver.id };
}

/**
 * The keys the approve body carries, and the only ones a `422` may mark a
 * control for. `routeFieldErrors` partitions on exactly this list, so a path
 * naming something this form does not render lands in the banner instead of
 * highlighting nothing.
 *
 * ✅ **These are the body's TOP LEVEL, and the paths come back undotted.**
 * Settled from source on 2026-08-29 rather than guessed: `approveCompanySchema`
 * is a flat `.strict()` object — there is no `salesDefaults` wrapper, and this
 * client was sending one — and the backend's `toDetails` emits
 * `issue.path.join('.')`, which over a flat schema is the bare key. So a refusal
 * on any of these marks its own control.
 *
 * ⚠️ **`statedApproverId` is on the list because the page RENDERS that control**,
 * and it is the one key here that can be refused for a reason nothing on the form
 * did wrong: the server re-checks group membership **inside the deciding
 * transaction**, so an approver removed between this page loading and the button
 * being pressed is refused. That refusal is a `422` on `path: 'statedApproverId'`
 * — deliberately not the opaque `410`, because it is actionable — and it must
 * mark the picker, which is the thing the reader has to change.
 */
const APPROVE_KEYS = [
  'xeroSalesLineAmountType',
  'xeroSalesTaxType',
  'currencyId',
  'xeroBrandingThemeId',
  // The approver's Billing name correction (owner 2026-10-27): 422
  // `BILLING_NAME_REQUIRED` and 409 `COMPANY_BILLING_NAME_TAKEN` both carry
  // `path: 'billingName'` and mark the box.
  'billingName',
  'statedApproverId',
] as const;

/**
 * The reject body's keys — a different, smaller set, so the two are not one list.
 *
 * `reason` routes to the dialog's own banner rather than to the textarea, which
 * is why it appears here at all: naming it keeps it out of `unroutable`, where it
 * would be indistinguishable from a path this page does not render.
 */
const REJECT_KEYS = ['reason', 'statedApproverId'] as const;

/**
 * Split a failure into the controls it marks and the sentences it banners.
 *
 * ⚠️ **The banner CONCATENATES, it does not choose.** `formMessages` and the
 * unroutable details can both be non-empty at once, and a `??` between two
 * messages that can both be true is a silent drop — the reader loses one of
 * them with nothing indicating there was more to read.
 *
 * `formMessage`/`message` is the LAST resort, reached only when the error would
 * otherwise render nowhere at all: on a 422 `message` is just "Validation
 * failed", which is worse than any of the alternatives above it and must never
 * outrank them. Ordered by specificity, deliberately — a fallback chain ordered
 * by whichever shape was met first is what breaks when the server's errors get
 * better.
 */
function describeFailure(
  error: ApiError | Error | null,
  keys: readonly string[],
): {
  fields: Record<string, string>;
  banner: string[];
} {
  if (!error) return { fields: {}, banner: [] };
  if (!(error instanceof ApiError)) return { fields: {}, banner: [error.message] };

  const routed = error.routeFieldErrors(keys);
  const banner = [...error.formMessages, ...routed.unroutable];
  if (banner.length === 0 && Object.keys(routed.fields).length === 0) {
    banner.push(error.formMessage ?? error.message);
  }
  return { fields: routed.fields, banner };
}

const STROKE = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

function AlertIcon({ className = 'mt-px h-4 w-4 shrink-0' }: { className?: string }) {
  return (
    <svg {...STROKE} className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4" />
      <path d="M12 16h.01" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg {...STROKE} className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="m8.5 12.5 2.5 2.5 4.5-5" />
    </svg>
  );
}

/**
 * The rejected counterpart of {@link CheckIcon}.
 *
 * ⚠️ **It exists so the two settled screens differ by SHAPE, not only by tone.**
 * Approved is a green tick; rejected was previously grey with no icon at all,
 * and "no icon" is a weaker signal than a different one — a reader glancing at a
 * screen they have loaded twice needs the two to look unalike at a distance.
 * The words still carry the meaning, per the house rule that colour is never the
 * only signal; this is a second signal, not the first.
 */
function CrossIcon() {
  return (
    <svg {...STROKE} className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="m9 9 6 6M15 9l-6 6" />
    </svg>
  );
}

/**
 * The signed-out wordmark: the full lockup, which carries the product name and
 * the descriptor inside the artwork. It is an image of text, so its accessible
 * name comes from `EmergeLockup`'s `aria-label` rather than from a sibling
 * element — see that file before splitting it back apart.
 */
function Masthead() {
  return (
    <header>
      <EmergeLockup className="w-[196px] max-w-full" />
    </header>
  );
}

/**
 * A terminal screen: masthead, one heading, one explanation, nothing to press.
 *
 * ⚠️ **There is deliberately no form and no second submit on any of these.**
 * After a decision the token is spent, so pressing anything again cannot
 * succeed — and a disabled button that can never enable is a worse answer than
 * no button at all.
 */
function Outcome({
  tone,
  icon,
  title,
  children,
}: {
  tone: 'success' | 'neutral' | 'danger';
  icon?: ReactNode;
  title: string;
  children: ReactNode;
}) {
  const toneClass = {
    success: 'border-success-border bg-success-soft text-success',
    neutral: 'border-border bg-surface-sunken text-fg-secondary',
    danger: 'border-danger-border bg-danger-soft text-danger',
  }[tone];

  return (
    <Panel>
      <PanelBody density="comfortable">
        <Masthead />
        <div className={`mt-6 flex gap-3 rounded-md border px-3 py-3 ${toneClass}`}>
          {icon}
          <div className="min-w-0">
            <h1 className="m-0 text-base font-semibold break-words">{title}</h1>
            <div className="mt-1.5 space-y-1.5 text-sm break-words">{children}</div>
          </div>
        </div>
      </PanelBody>
    </Panel>
  );
}

/**
 * The identity step: the approver says which of the recipients they are.
 *
 * ⚠️ **A STATED name, not a check.** Every word rendered here was chosen against
 * that — see {@link IDENTITY_TITLE}, and {@link IDENTITY_REQUIRED_REASON} for
 * why the buttons start inert. (*SUPERSEDED 2026-09-05: this also pointed at
 * `SHARED_LINK_NOTE`, removed under the stricter helper-text ruling — so nothing
 * on screen now tells the approver the link is shared.*) The control is
 * a plain dropdown for the same reason it is not a text box: a free-text name
 * would record something nobody could match to a person, while the list is
 * exactly the set the request was sent to, so the recorded value is always a
 * real approver even though *which* one is taken on trust.
 *
 * ⚠️ **Rendered only over a NON-EMPTY list, and it must not grow a fallback that
 * invents one.** An approvers list this page assembled would be a claim about who
 * may sign off a customer, made by a client with no session — and every id in it
 * would be refused, because the server checks the real group inside the deciding
 * transaction. Where the list is empty the page says so instead; see
 * {@link NoApprovers}.
 */
function ApproverField({
  approver,
  index,
  error,
}: {
  /** The signed-in person, already matched against the server's own list. */
  approver: CompanyApprovalApprover;
  /** Their position in that list, so the fallback label matches the old one. */
  index: number;
  /**
   * The server's own `422` on `path: 'statedApproverId'`, when there is one.
   *
   * ⚠️ **It reaches this block even though there is no longer a control to
   * fix**, and that is deliberate: the one way to provoke it now is an approver
   * being removed from the group while this page sits open, and the reader has
   * to be told the decision was refused rather than watch a button do nothing.
   * The recovery is a reload, not an edit.
   */
  error?: string;
}) {
  return (
    <Panel>
      <PanelHeader title={IDENTITY_TITLE} />
      <PanelBody>
        {/*
          * 🔴 **PLAIN TEXT, NOT A DISABLED PICKER.** *SUPERSEDED: a `Select` of
          * every approver, with a "Choose your name" row.* The page is behind
          * `RequireAuth` now, so the session already says who this is; a
          * disabled dropdown reads as something the reader has not earned the
          * right to use, where text reads as a fact. Same ruling as the
          * read-only account lead on the quote and invoice forms, and as `kind`
          * on an edited future billable.
          *
          * ⚠️ **`Field` is not used here BECAUSE THERE IS NO CONTROL TO LABEL.**
          * A `<label>` pointing at nothing is a broken association for a screen
          * reader; the caption is an ordinary paragraph.
          */}
        <div className="grid min-w-0 gap-1.5">
          <p className={IDENTITY_VALUE_LABEL}>Deciding as</p>
          <p className="m-0 text-sm font-medium text-fg">{approverLabel(approver, index)}</p>
          {error !== undefined && (
            <p role="alert" className="m-0 text-xs text-danger">
              {error}
            </p>
          )}
        </div>
      </PanelBody>
    </Panel>
  );
}

/**
 * ⚠️ **SIGNED IN, AND NOT AN APPROVER — a refusal that NAMES WHO CAN.**
 *
 * Reached when the session resolves to somebody absent from the server's
 * approvers list. It is a real state rather than an edge: the link is shared by
 * email and gets forwarded, and any of the twelve staff accounts can open it.
 *
 * ⚠️ **It names the GROUP, never the members.** `CompanyApprovalApprover`
 * carries `{id, name}` and no email precisely so this page does not publish a
 * staff directory to whoever holds a token — and this screen is reached by
 * exactly the people who should not be reading one. Saying "the approvers
 * group decides this" is actionable (ask to be added) without listing anybody.
 *
 * ⚠️ **THE DECISION BUTTONS DO STILL RENDER, DISABLED, WITH
 * {@link IDENTITY_REQUIRED_REASON} BESIDE THEM — measured, after this comment
 * first claimed the opposite.** They hang off `canDecide` (is the group
 * non-empty), not off who is reading, so reaching this screen leaves them
 * present and inert.
 *
 * **That is the right arrangement and was left alone.** A bare `disabled` makes
 * a permission denial and a broken control identical — the rule this file
 * already follows for `denialReason()` and the DocuSign `viewer` — so the pair
 * plus a sentence naming the real reason beats hiding them, which would read as
 * the page having failed to load its own actions. The request itself is
 * rendered in full above, so a colleague can still read what was asked for and
 * chase the right person.
 *
 * ⚠️ *SUPERSEDED, and it was written in this same change: "NO BUTTONS, AND DO
 * NOT ADD A DISABLED PAIR … a greyed-out Approve suggests the decision is
 * theirs to unlock."* It described an intention, not the page — the buttons were
 * never wired to this branch. **Kept rather than deleted because a comment
 * forbidding what the code does is the exact artefact that sends the next
 * reader to "fix" working behaviour.**
 */
function NotAnApprover({ signedInAs }: { signedInAs: string }) {
  return (
    <Panel>
      <PanelHeader title={IDENTITY_TITLE} />
      <PanelBody>
        <div className="grid min-w-0 gap-1.5">
          <p className={IDENTITY_VALUE_LABEL}>Signed in as</p>
          <p className="m-0 text-sm font-medium text-fg">{signedInAs}</p>
          <p className="m-0 text-sm text-fg-secondary">
            Only the approvers group can approve or reject a new school, and this account is not
            in it. Ask an administrator to add you, or forward this link to somebody who is.
          </p>
        </div>
      </PanelBody>
    </Panel>
  );
}

/**
 * ⚠️ **NOBODY CAN DECIDE THIS, so the page says so instead of offering buttons.**
 *
 * Reached when the approvers list is EMPTY — and, defensively, when it is absent
 * altogether. See the comment at the `approvers` read for why those two collapse.
 *
 * ## Why this screen has to exist at all
 *
 * `statedApproverId` is **required on both approve and reject**, and the server
 * refuses any id that is not a current member of the approvers group. With an
 * empty group there is no id this page could send that would be accepted — so
 * every button it could render is an action that cannot succeed. The previous
 * version of this page rendered the form with **both buttons live** in exactly
 * this state, because an empty list used to mean "this deployment does not ask
 * for a name". Under the shared link it means the opposite: nobody was mailed,
 * nobody can be recorded, and pressing Approve buys a `422` naming a field with
 * no control on screen.
 *
 * ⚠️ **It is the same condition as `approversFound: 0` on the resend response**,
 * seen from the other end of the same link — which is why the sentence here and
 * the `danger` branch of `describeResendOutcome` say the same thing in the same
 * order: nothing was sent, and nothing can be approved until somebody is added to
 * the group. The two are read by different people and must not disagree.
 *
 * ⚠️ **Toned NEUTRAL, not danger, and carrying no retry.** The reader did nothing
 * and nothing failed, so a red box reading "your approval did not work" would be
 * false — the same call as {@link GONE_TITLE}. What it must not do is imply
 * waiting will help: the fix is a person being added to a group, by somebody
 * else, which is why the only action offered is who to contact.
 *
 * ⚠️ **It names the cause without naming the people.** "Nobody is in the
 * approvers group" is strictly less than the names this page publishes when the
 * group is populated, so it leaks nothing new — and without it the reader is told
 * a link is unusable with no idea whether that is their problem to solve.
 */
function NoApprovers() {
  return (
    <Panel>
      <PanelBody density="comfortable">
        <div className="flex gap-3 rounded-md border border-border bg-surface-sunken px-3 py-3 text-fg-secondary">
          <AlertIcon className="mt-0.5 h-5 w-5 shrink-0" />
          <div className="min-w-0">
            <h2 className="m-0 text-base font-semibold break-words text-fg">
              This request cannot be decided yet
            </h2>
            <div className="mt-1.5 space-y-1.5 text-sm break-words">
              <p>
                Nobody is in the approvers group, so there is no name to record a decision against.
              </p>
              <p>Somebody must be added to that group first. {CONTACT_LINE}</p>
            </div>
          </div>
        </div>
      </PanelBody>
    </Panel>
  );
}

export function CompanyApprovalPage() {
  /*
   * ⚠️ THE TOKEN ARRIVES BY TWO DIFFERENT ROUTES, AND THE PATH ONE IS THE REAL
   * ONE. Both are mounted in `App.tsx`; read that comment for why neither may
   * be deleted.
   *
   * `/company-approvals/:token` is what the backend puts in the email —
   * `company-approvals.service.ts` builds the link with the token as a PATH
   * SEGMENT. `/approve-company?token=` is the older address this app documented
   * for itself, kept as an alias because it may have been shared.
   *
   * ⚠️ ORDER MATTERS, AND THE PATH WINS. On `/company-approvals/<token>` there
   * is no query string at all, so the fallback is unreachable; on the alias
   * there is no path param. They cannot both be present from any link we send,
   * and preferring the path means a hand-edited `?token=` appended to a real
   * link cannot override the credential the email actually carried.
   *
   * ⚠️ URL-UNSAFE CHARACTERS SURVIVE, in both directions. The backend
   * `encodeURIComponent`s the token when building the link; React Router
   * DECODES a path param, and `URLSearchParams.get` decodes a query value — so
   * `token` here is the RAW token either way, which is exactly what
   * `api.getCompanyApproval` re-encodes on its way back out. Do not decode it
   * again here: a token containing a literal `%` would be corrupted by the
   * second pass, and a corrupted token leaves by the opaque 410 door with
   * nothing on screen suggesting the address was mangled rather than spent.
   *
   * `useSearchParams` takes no argument, per the house rule: its first
   * parameter is a *default init*, not options, and passing `{ replace: true }`
   * writes a literal `replace=true` into the query string.
   */
  const { token: pathToken } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const token = pathToken ?? searchParams.get('token');

  /*
   * The signed-in person, who since 2026-09-11 is who the decision is recorded
   * against — see the approver match below. `RequireAuth` guarantees one, so
   * this is never the loading-or-absent case a page outside the gate has to
   * handle.
   */
  const { user } = useAuth();

  /*
   * The phase is stored WITH the token it describes, and "loading" is DERIVED
   * from the two disagreeing rather than written by the effect.
   *
   * Two things fall out of that. There is no synchronous `setState` in the
   * effect, so no cascading render; and a token that changes under the page
   * shows loading on the very next render instead of one stale frame of the
   * previous school — which on an approval screen is a frame showing the wrong
   * company name above a live Approve button.
   */
  const [state, setState] = useState<{ forToken: string | null; phase: Phase }>({
    forToken: null,
    phase: { kind: 'loading' },
  });
  const phase: Phase = state.forToken === token ? state.phase : { kind: 'loading' };
  const setPhase = (next: Phase) => setState({ forToken: token, phase: next });

  /*
   * The four controls.
   *
   * ⚠️ `lineAmountType` has NO blank member and the other three do, which mirrors
   * the contract exactly: `xeroSalesLineAmountType` is a bare required enum
   * server-side while the rest are nullable. `''` on the other three is "use
   * Xero's organisation settings" and maps to `null` on the way out — a real
   * answer, not an unfilled box. `EXCLUSIVE` is the owner's ruling for a new
   * company, so it is the initial value.
   */
  const [lineAmountType, setLineAmountType] = useState<XeroLineAmountType>('EXCLUSIVE');
  const [taxType, setTaxType] = useState('');
  /** The currency MASTER ROW ID as a string, or `''`. Never a three-letter code. */
  const [currencyId, setCurrencyId] = useState('');
  const [brandingThemeId, setBrandingThemeId] = useState('');
  /*
   * The Billing name the Xero Contact will be created under, editable by the
   * approver (owner 2026-10-27). Seeded on the load with the stored value;
   * sent only when it differs from that seed, so an untouched box writes
   * nothing. Blank is sent as `''` and refused by the server, at the field.
   */
  const [billingNameDraft, setBillingNameDraft] = useState('');
  const [billingNameSeed, setBillingNameSeed] = useState('');

  /**
   * Which of the three seedable controls the approver has taken over.
   *
   * ⚠️ **"Untouched" is tracked EXPLICITLY, and the flag is set even when the
   * approver CLEARS the box back to {@link USE_ORG_SETTINGS}.** That is the
   * whole point of tracking it rather than testing `value === ''`: a cleared
   * control and an unseeded one hold the same string, so a seed that keyed on
   * the value would re-fill a box somebody had deliberately emptied and the
   * field would fight the person using it. Same rule as the seeded document name
   * on a new quote or deal.
   *
   * ⚠️ **A ref, not state.** Nothing renders from it, and a re-render must never
   * be what decides whether a seed happens — the seed runs once, on the load, in
   * the `.then()` below.
   */
  const touched = useRef({
    taxType: false,
    currencyId: false,
    brandingThemeId: false,
    billingName: false,
  });

  /**
   * 🔴 **WHO IS DECIDING IS NO LONGER TYPED IN — IT IS THE SESSION.**
   * *SUPERSEDED, and the state went with it: `const [statedApproverId,
   * setStatedApproverId] = useState('')`, "who the reader SAYS they are — the
   * approver's row id as a string, or `''` for not said yet."*
   *
   * The page is behind `RequireAuth` since 2026-09-11 (owner's instruction), so
   * there is a signed-in person and nothing to ask. The id is matched out of
   * the server's own approvers list below rather than read off the session
   * alone, which is what keeps the old rule intact: **the name printed is one
   * the SERVER offered**, so a session belonging to somebody who is not an
   * approver resolves to `null` and is refused rather than rendered.
   *
   * ⚠️ **There is no `useState` here on purpose — it is derived every render.**
   * Holding a copy would reintroduce exactly the staleness the old comment
   * warned about, with no control left that could change it.
   */

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState<'approve' | 'reject' | null>(null);
  /*
   * ⚠️ The failure is stored WITH the action that caused it. Without that, a
   * rejection refused by the server renders its sentence beside the Approve
   * button, which reads as "approving is broken" — a refusal attributed to the
   * wrong verb is worse than no refusal, because the reader acts on it.
   */
  const [submitError, setSubmitError] = useState<{
    from: 'approve' | 'reject';
    error: ApiError | Error;
  } | null>(null);

  /*
   * ⚠️ Aborts the in-flight DECISION when the page goes away, and nothing else.
   *
   * A `useRef` rather than an effect-scoped controller because the request is
   * started by a click, not by a render — an approver who presses Approve and
   * navigates away would otherwise leave a promise resolving into a component
   * that is gone. The write itself is NOT recalled by this (the server has it),
   * which is precisely why nothing in the UI offers to cancel a decision: see
   * the reject dialog's `onClose`.
   */
  const decisionAbort = useRef<AbortController | null>(null);
  useEffect(() => () => decisionAbort.current?.abort(), []);

  useEffect(() => {
    /*
     * No state is set for a missing token: "there is no token" is derivable
     * during render, and deriving it there rather than round-tripping it
     * through an effect is both a render fewer and one less way for the two to
     * disagree. The render guard below is the whole handling.
     */
    if (!token) return;

    const controller = new AbortController();
    const { signal } = controller;
    /*
     * Local rather than the component's `setPhase`, which is rebuilt every
     * render: depending on that one would re-run this effect — and therefore
     * re-fetch — on every keystroke in the form. `setState` is a stable setter,
     * so the dependency list stays honestly `[token]`.
     */
    const commit = (next: Phase) => setState({ forToken: token, phase: next });

    api
      .getCompanyApproval(token, signal)
      .then((response) => {
        if (signal.aborted) return;
        const view = response.data;
        /*
         * Read the decision from `status`, never inferred from anything else. A
         * request somebody already settled must show its OUTCOME — a form that
         * cannot succeed is the one thing this screen must not be.
         */
        if (view.status !== 'pending') {
          commit({
            kind: 'settled',
            outcome: view.status,
            companyName: view.company.name,
            billingName: view.company.billingName,
            byMe: false,
            statedName: null,
          });
          return;
        }

        /*
         * Seed the sales-defaults controls from the company's BILLING REGION.
         *
         * ⚠️ Done HERE — once, on the load — and not in an effect over the
         * rendered phase. A seed that re-ran on render would have to decide
         * every time whether the current value is the approver's or the
         * server's, which is exactly the judgement `touched` exists to remove.
         * One load, one seed, and after that the controls are the reader's.
         *
         * The `touched` guards are still checked rather than assumed: a token
         * that changes under an open page refetches, and the approver may have
         * answered a control already. A value somebody typed outranks a default,
         * always.
         */
        const seed = seedFromBillingRegion(view.company, view.xeroOptions.currencies);
        if (seed !== null) {
          if (!touched.current.taxType) setTaxType(seed.taxType);
          if (!touched.current.currencyId) setCurrencyId(seed.currencyId);
          if (!touched.current.brandingThemeId) setBrandingThemeId(seed.brandingThemeId);
        }
        /*
         * The stored Billing name, falling back to the Company name only when
         * blank — exactly what the Xero Contact would be created under.
         * `undefined` = an older API, which takes no `billingName`: no box.
         */
        if (view.company.billingName !== undefined) {
          const stored = view.company.billingName?.trim() || view.company.name.trim();
          setBillingNameSeed(stored);
          if (!touched.current.billingName) setBillingNameDraft(stored);
        }

        commit({ kind: 'ready', view });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        /*
         * 410 is the only status meaning "there is nothing to do here". Anything
         * else is a request that did not land, which is a different screen, a
         * different tone, and a retry that is worth offering.
         */
        if (caught instanceof ApiError && caught.status === 410) {
          commit({ kind: 'gone' });
          return;
        }
        commit({
          kind: 'unavailable',
          message:
            caught instanceof Error ? caught.message : 'The approval request could not be loaded.',
        });
      });

    return () => controller.abort();
  }, [token]);

  /**
   * Both decisions fail the same way, so the handling lives once.
   *
   * ⚠️ **`409` and `410` mean opposite things about this reader's link, and the
   * whole two-approver design turns on the difference.**
   *
   * - `410` — the link itself is finished. Spent, expired, or pointing at
   *   nothing. The `gone` screen; the reader did nothing wrong.
   * - `409 COMPANY_APPROVAL_ALREADY_DECIDED` — the link is **still live and
   *   still unspent**. A colleague holding the *other* token settled the request
   *   first, and the server rolled this transaction back rather than burning a
   *   valid credential to say so. Killing the sibling token would have been the
   *   naive way to settle a request for both approvers, and it would tell a
   *   legitimate colleague their legitimate link was invalid.
   *
   * ⚠️ **The shared link makes the `409` RARE, not wrong, and the branch stays.**
   * Where there is one link rather than one per approver, the first decision
   * spends it and everyone else meets the `410` above — so this path narrows to
   * a superseded round whose older link is still live. It is kept for the reason
   * every refusal branch here is kept: the deployment can change under a page
   * somebody already has open, and a branch that cannot fire costs nothing while
   * a missing one reports the wrong outcome to somebody acting on it.
   *
   * So a `409` is answered by **re-reading the `GET`** rather than by guessing
   * the outcome from the refusal's prose. The token is unspent, so the read
   * succeeds and reports what was actually recorded — which may be an APPROVAL
   * even when this reader pressed Reject. Reporting the verb pressed rather than
   * the outcome recorded is how somebody comes away believing they blocked a
   * school that is live.
   */
  function handleDecisionFailure(from: 'approve' | 'reject', caught: unknown) {
    if (caught instanceof ApiError && caught.status === 410) {
      setPhase({ kind: 'gone' });
      return;
    }
    if (caught instanceof ApiError && caught.status === 409) {
      setRejectOpen(false);
      /*
       * If the re-read fails there is nothing honest left to say about the
       * outcome, so the server's own refusal stands. It says a decision was
       * already made without claiming which, which is strictly better than a
       * screen naming an outcome nothing confirmed.
       */
      api
        .getCompanyApproval(token as string)
        .then((response) => {
          const settled = response.data;
          if (settled.status === 'pending') {
            setSubmitError({ from, error: caught });
            return;
          }
          setPhase({
            kind: 'settled',
            outcome: settled.status,
            companyName: settled.company.name,
            billingName: settled.company.billingName,
            byMe: false,
            statedName: null,
          });
        })
        .catch(() => setSubmitError({ from, error: caught }));
      return;
    }
    setSubmitError({
      from,
      error: caught instanceof Error ? caught : new Error('The decision could not be saved.'),
    });
  }

  /*
   * Derived, not a `Phase` member: the URL carrying no token is a fact about
   * the address bar, knowable on every render, and nothing was attempted — so
   * this is not a failure screen and must not be toned as one.
   */
  if (!token) {
    return (
      <Shell>
        <Outcome tone="neutral" title="This link is incomplete">
          <p>
            The address is missing its approval token. Open the link from the email exactly as it
            was sent.
          </p>
          <p>{CONTACT_LINE}</p>
        </Outcome>
      </Shell>
    );
  }

  if (phase.kind === 'loading') {
    return (
      <Shell>
        <Panel>
          <PanelBody density="comfortable">
            <Masthead />
            {/* Skeletons in the real shape: heading, requester line, then four controls. */}
            <div className="mt-6 space-y-6" aria-busy="true">
              <div className="space-y-2">
                <Skeleton className="h-6 w-3/5" />
                <Skeleton className="h-4 w-2/5" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {[0, 1, 2, 3].map((row) => (
                  <div key={row} className="space-y-1.5">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-9 w-full" />
                  </div>
                ))}
              </div>
            </div>
          </PanelBody>
        </Panel>
      </Shell>
    );
  }

  if (phase.kind === 'gone') {
    return (
      <Shell>
        <Outcome tone="neutral" title={GONE_TITLE}>
          {/*
           * All four reasons in one sentence, because the server does not say
           * which — then, plainly, that nothing happened just now. Somebody
           * arriving here after a successful decision must not read it as a
           * failure, and somebody arriving on a spent link must not conclude
           * they have accidentally decided something.
           *
           * ⚠️ The order is by likelihood, not by importance, and it stops
           * short of naming a cause. "Already used to decide this request"
           * leads because a shared link makes it the common case, and it stays
           * a "may" because this screen cannot distinguish it from a link that
           * expired, was replaced, or never existed.
           */}
          <p>
            It may already have been used, expired, or been replaced.{' '}
            <strong className="font-semibold">No decision was recorded just now.</strong>
          </p>
          <p>{CONTACT_LINE}</p>
        </Outcome>
      </Shell>
    );
  }

  if (phase.kind === 'unavailable') {
    return (
      <Shell>
        <Outcome
          tone="danger"
          icon={<AlertIcon className="mt-0.5 h-5 w-5 shrink-0" />}
          title="The approval request could not be loaded"
        >
          <p>{phase.message}</p>
          <p>
            {/*
             * A reload rather than a state reset: this page holds no other work
             * to lose, and reloading also clears anything wedged in the client.
             */}
            <Button variant="secondary" size="sm" onClick={() => window.location.reload()}>
              Try again
            </Button>
          </p>
        </Outcome>
      </Shell>
    );
  }

  if (phase.kind === 'settled') {
    const approved = phase.outcome === 'approved';
    return (
      <Shell>
        <Outcome
          tone={approved ? 'success' : 'neutral'}
          icon={approved ? <CheckIcon /> : <CrossIcon />}
          /*
           * ⚠️ **"was rejected", not "was not approved".** The negated positive
           * is one missed word away from its opposite on a small screen, and
           * this is the screen an approver may reload having forgotten which
           * button they pressed. The two titles must not be able to be misread
           * as each other.
           */
          title={
            approved ? `${phase.companyName} is approved` : `${phase.companyName} was rejected`
          }
        >
          {/*
           * Both names, as the pre-decision view shows them (owner 2026-10-27):
           * the heading carries the Company name, this the Billing name Xero
           * gets. `undefined` = an older API: show nothing.
           */}
          {phase.billingName !== undefined && (
            <dl className="min-w-0">
              <dt className="text-xs font-medium">{BILLING_NAME_LABEL} (Xero)</dt>
              <dd className="mt-0.5 break-words">
                {phase.billingName?.trim() ? phase.billingName.trim() : 'Not recorded'}
              </dd>
            </dl>
          )}
          {/*
           * ⚠️ Reports the RECORDED OUTCOME, never the verb the reader pressed.
           * Where this page did the write it may say so; where it merely read
           * `status`, somebody else decided and claiming otherwise would put the
           * wrong name against the decision.
           *
           * The decision word is repeated in the body rather than left to the
           * title alone: a heading carrying the company name is read as a label
           * for the page, and the one thing that must survive a skim is which of
           * the two things happened.
           */}
          {phase.byMe ? (
            <p>
              <strong className="font-semibold">{approved ? 'Approved' : 'Rejected'}</strong>
              {/*
               * The stated name, worded as what it is: what was recorded, from
               * what was selected. Not "approved by", which would assert the
               * page had established the person rather than been told.
               */}
              {phase.statedName
                ? `, recorded against ${phase.statedName}.`
                : ', and your decision has been recorded.'}
              {approved ? ' Your Xero sales defaults were saved with it.' : ''}
            </p>
          ) : (
            <p>This request was already decided, so nothing was recorded just now.</p>
          )}
          <p>This link is now closed.</p>
        </Outcome>
      </Shell>
    );
  }

  const { view } = phase;
  const { company, requestedBy, requestedAt, matches, expiresAt, xeroOptions } = view;
  // The Billing name (the Xero Contact Name); `undefined` on an older API.
  const approvalBillingName = company.billingName;
  const { taxRates, brandingThemes, currencies } = xeroOptions;
  /*
   * ⚠️ **NOT DESTRUCTURED WITH THE REST, because the key is not on the type.**
   * `types.ts` is owned elsewhere and has not been widened, so this is read
   * through a guard rather than named above — see {@link readTypedCountryNames},
   * which also explains why the guard is right even after the key is declared.
   */
  const typedCountryNames = readTypedCountryNames(view);

  /*
   * ⚠️ **AN EMPTY LIST MEANS NOBODY CAN DECIDE — it is not permission to skip
   * the question.** That reverses what this line used to mean, and the reversal
   * is the whole point of the shared link.
   *
   * `statedApproverId` is REQUIRED on both verbs, so there is no longer a
   * deployment in which this page decides anything without a name. An empty group
   * is therefore not "do not ask"; it is "there is no acceptable answer", and the
   * page renders {@link NoApprovers} rather than a form whose every submission the
   * server refuses.
   *
   * ⚠️ **`?? []` IS DEFENSIVE, NOT A STATEMENT ABOUT THE CONTRACT.** The field is
   * required — the server always sends it — so this only catches a server OLDER
   * than the shared-link change, where reading `.length` off `undefined` would
   * crash the one page whose whole audience cannot report a bug. Absent is
   * deliberately folded into empty rather than given its own screen, for two
   * reasons: the reader's next action is identical (contact whoever asked), and
   * this page cannot honestly name the cause to them — "the server did not send
   * the approvers list" is not a sentence an external approver can act on.
   *
   * ⚠️ **AND THAT COSTS NOTHING, because this page is version-locked to the new
   * contract anyway.** Against an older server, sending `statedApproverId` at all
   * is a `422 Unrecognized key`, so there is no version of this page that works
   * against both. **The backend deploys first** — that is a deploy-order fact, not
   * a fallback this page can absorb.
   *
   * ⚠️ **`?? []` is correct HERE and would be a defect twenty lines below**, on
   * `ApprovalOptionList`, where "we could not read it" and "there is nothing in
   * it" are facts the reader acts on differently.
   */
  const approvers = view.approvers ?? [];
  /**
   * Whether this page can produce a decision the server will accept. Both
   * buttons, the identity step and the sales-defaults form hang off it together —
   * offering any one of them without the others is offering half an action.
   */
  const canDecide = approvers.length > 0;
  /*
   * Resolved by looking the id back up, never trusted from state alone — so a
   * selection left over from a list that has since changed reads as unchosen
   * rather than as a name nobody offered.
   */
  /*
   * ⚠️ **MATCHED ON `id`, AND ONLY AGAINST THE LIST THE SERVER SENT.**
   * *SUPERSEDED: `approvers.find((row) => String(row.id) === statedApproverId)`.*
   *
   * The session says who the reader is; this says whether that person is an
   * approver. Both halves are needed — trusting the session alone would print a
   * name the server never offered and then collect a `422` on submit, and
   * `CompanyApprovalApprover` is `{id, name}` with no email precisely so a
   * reader cannot be shown their colleagues' addresses. **Never match on the
   * NAME**: two staff can share one, and a name is the weakest key in this
   * system.
   *
   * `user` is non-null in practice — `RequireAuth` is above this page — but it
   * is read optionally rather than asserted, so a future unwrapping of the
   * route degrades to "not an approver" and a refusal instead of a crash.
   */
  const sessionApprover = approvers.find((row) => row.id === user?.id) ?? null;
  const chosenApprover = sessionApprover;
  const identityMissing = chosenApprover === null;
  const statedName =
    chosenApprover === null
      ? null
      : approverLabel(chosenApprover, approvers.indexOf(chosenApprover));
  /*
   * Both decisions are held back by the same condition, and the reason is
   * spelled once so the two buttons cannot come to explain themselves
   * differently. `null` when there is nothing to explain — a caller then renders
   * nothing rather than an empty slot.
   */
  const decisionBlockedReason = identityMissing ? IDENTITY_REQUIRED_REASON : null;

  // Each failure renders only against the action that produced it.
  const approveFailure = describeFailure(
    submitError?.from === 'approve' ? submitError.error : null,
    APPROVE_KEYS,
  );
  const rejectFailure = describeFailure(
    submitError?.from === 'reject' ? submitError.error : null,
    REJECT_KEYS,
  );
  const markedFields = approveFailure.fields;
  /*
   * The picker is shared by both verbs, so its refusal can arrive from either.
   *
   * ⚠️ **This `??` is NOT the silent-drop shape**, and the reason is structural
   * rather than a promise: `submitError` holds ONE `from`, so at most one of these
   * two objects is ever populated. There is no state in which both sides are true
   * and one is discarded.
   */
  const statedApproverError =
    approveFailure.fields.statedApproverId ?? rejectFailure.fields.statedApproverId;

  /*
   * ⚠️ **Nothing is validated client-side, and that is deliberate.**
   *
   * Three of the four values are nullable — `null` is "use Xero's organisation
   * settings", a real answer — and the fourth is a `<select>` over a closed enum
   * with no blank member, so it cannot hold anything the server refuses.
   * Currency and letterhead now come from the server's own lists, so the format
   * check this page used to run against a typed currency code has nothing left
   * to check. Marking any of them required here would enforce client-side what
   * the server permits, which fails *silently*: the approver cannot submit,
   * nothing errors anywhere, and they conclude the page is broken.
   */

  async function handleApprove(event: FormEvent) {
    event.preventDefault();
    /*
     * ⚠️ Unreachable through the UI — the button is disabled until a name is
     * chosen — and it is here because `statedApproverId` is REQUIRED, so this is
     * what makes the type check honest rather than a cast. Returning silently is
     * right precisely because it cannot happen: there is no state in which the
     * reader pressed a live button and gets nothing.
     */
    if (chosenApprover === null) return;
    setSubmitError(null);

    decisionAbort.current?.abort();
    const controller = new AbortController();
    decisionAbort.current = controller;

    setSubmitting('approve');
    try {
      /*
       * A FLAT body — the four keys at the top level. The schema is `.strict()`,
       * so the `{salesDefaults: …}` wrapper this page used to send was a 422 on
       * every approval.
       *
       * Every key is sent, each blank as `null`, except the line-amount type
       * which has no blank: the required key forces the form to decide about
       * each one, and the nullable value is what carries "use organisation
       * settings" rather than "this form forgot to ask".
       */
      const response = await api.approveCompany(
        token as string,
        {
          xeroSalesLineAmountType: lineAmountType,
          xeroSalesTaxType: taxType.trim() || null,
          currencyId: currencyId === '' ? null : Number(currencyId),
          xeroBrandingThemeId: brandingThemeId.trim() || null,
          // Only when the approver changed it (owner 2026-10-27).
          ...(approvalBillingName !== undefined && billingNameDraft.trim() !== billingNameSeed
            ? { billingName: billingNameDraft.trim() }
            : {}),
          /*
           * The stated identity — REQUIRED, so it is always present. It is
           * spread from the one helper rather than written inline so this page
           * spells the key in a single place; the last correction to it was a
           * rename because of that.
           */
          ...statedIdentity(chosenApprover),
        },
        controller.signal,
      );
      if (controller.signal.aborted) return;
      /*
       * The RECORDED status, read back from the response rather than assumed
       * from the button. Approve answers 200 with the settled view, so there is
       * no reason to assert an outcome the server has just described.
       */
      setPhase({
        kind: 'settled',
        outcome: response.data.status === 'rejected' ? 'rejected' : 'approved',
        companyName: response.data.company.name,
        billingName: response.data.company.billingName,
        byMe: true,
        statedName,
      });
    } catch (caught) {
      if (controller.signal.aborted) return;
      handleDecisionFailure('approve', caught);
    } finally {
      if (!controller.signal.aborted) setSubmitting(null);
    }
  }

  async function handleReject() {
    // Unreachable through the UI, for the same reason as `handleApprove` above.
    if (chosenApprover === null) return;
    setSubmitError(null);

    decisionAbort.current?.abort();
    const controller = new AbortController();
    decisionAbort.current = controller;

    setSubmitting('reject');
    try {
      // `reason` is an optional STRING, not a nullable one — omit it when blank.
      const reason = rejectReason.trim();
      const response = await api.rejectCompany(
        token as string,
        // The same identity fragment as approve, and required here too: a
        // rejection is the decision somebody is most likely to be asked about
        // later. `reason`, by contrast, stays optional and is omitted when blank.
        { ...(reason ? { reason } : {}), ...statedIdentity(chosenApprover) },
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setRejectOpen(false);
      setPhase({
        kind: 'settled',
        outcome: response.data.status === 'approved' ? 'approved' : 'rejected',
        companyName: response.data.company.name,
        billingName: response.data.company.billingName,
        byMe: true,
        statedName,
      });
    } catch (caught) {
      if (controller.signal.aborted) return;
      handleDecisionFailure('reject', caught);
    } finally {
      if (!controller.signal.aborted) setSubmitting(null);
    }
  }

  return (
    <Shell>
      <div className="space-y-4">
        <Panel>
          <PanelBody density="comfortable">
            <Masthead />

            <div className="mt-6">
              <p className="text-xs font-medium text-fg-muted">
                Approval requested
              </p>
              {/*
               * Full name and short name BOTH, never one instead of the other.
               * Identity is being ESTABLISHED here — the approver has not seen
               * this account before, and two schools of one university differ
               * only in the tail of the name.
               */}
              <h1 className="m-0 mt-1 text-xl font-semibold tracking-[-0.01em] break-words text-fg">
                {company.name}
              </h1>
              {company.shortName && (
                <p className="mt-0.5 text-sm break-words text-fg-muted">{company.shortName}</p>
              )}
              {/*
               * Approving creates the Xero Contact under the Billing name, so
               * the approver sees exactly what Xero will get (owner 2026-10-25,
               * N8). `undefined` = an older API: show nothing rather than
               * claim "Not recorded". Where the approve form renders, the
               * value is the editable box there instead (owner 2026-10-27).
               */}
              {approvalBillingName !== undefined && !canDecide && (
                <dl className="mt-3 min-w-0">
                  <dt className="text-xs font-medium text-fg-muted">
                    {BILLING_NAME_LABEL} (Xero)
                  </dt>
                  <dd className="mt-0.5 text-sm break-words text-fg">
                    {approvalBillingName?.trim() ? approvalBillingName.trim() : 'Not recorded'}
                  </dd>
                </dl>
              )}
            </div>

            <dl className="mt-5 grid gap-4 sm:grid-cols-2">
              <div className="min-w-0">
                <dt className="text-xs font-medium text-fg-muted">
                  Website
                </dt>
                <dd className="mt-0.5 text-sm break-words text-fg">
                  {company.website ? (
                    <ExternalTextLink
                      href={company.website}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="underline"
                    >
                      {company.website}
                    </ExternalTextLink>
                  ) : (
                    orDash(null)
                  )}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-xs font-medium text-fg-muted">
                  Requested
                </dt>
                <dd className="mt-0.5 text-sm break-words text-fg">
                  {/* `requestedBy` is nullable, and an unnamed requester is a real state. */}
                  {requestedBy ? `${personLabel(requestedBy)}, ` : ''}
                  {formatDateTime(requestedAt)}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-xs font-medium text-fg-muted">
                  Location
                </dt>
                {/*
                 * ⚠️ **`company.country`, NOT `company.billingCountry`** —
                 * changed 2026-08-31. The country of record is the foreign key
                 * the server derives this school's billing region, currency and
                 * Xero defaults from, and those are shown to this approver a few
                 * lines down; the free-text billing country was prose somebody
                 * typed, is no longer collected on a company, and disagreed with
                 * the key on 13 of 149 records — always by being the wrong one.
                 * Showing the address line beside a currency derived from a
                 * different field is the proxy-field failure exactly.
                 *
                 * The view serialises it as an already-resolved NAME
                 * (`country: string | null`), so there is nothing to look up.
                 * `null` is ordinary: no country recorded. `billingCity` is
                 * untouched — still free text, still a company's only city.
                 */}
                <dd className="mt-0.5 text-sm break-words text-fg">
                  {orDash([company.billingCity, company.country].filter(Boolean).join(', ') || null)}
                  {/*
                   * ⚠️ **INSIDE the Location field, under the value it explains,
                   * and NOT a fifth cell in this grid.** The grid is
                   * `sm:grid-cols-2`, so a fifth cell lands diagonally opposite
                   * the country it is about at every width above `sm` — and a
                   * caveat that has to be hunted for is one that gets attached to
                   * the wrong field by whoever finds it.
                   *
                   * ⚠️ **Rendered ONLY when there is something to say**, exactly
                   * like the duplicate-matches panel below. A permanent
                   * "Country typed: —" row would be a heading over an empty cell
                   * on the overwhelming majority of approvals — almost every
                   * company is created by staff, not by the form — and it would
                   * read as a field somebody forgot to fill in.
                   */}
                  {typedCountryNames.length > 0 && (
                    <div className="mt-2 border-l-2 border-border pl-2.5">
                      {/*
                       * ⚠️ **Neutral, never `warning`.** The matches panel below
                       * is toned `warning` because it is one. This is a fact, and
                       * an unresolved country must not become a nag, a gate, or a
                       * reason to delay a decision — the submission is stored and
                       * the institution is already interested either way.
                       */}
                      <p className="text-xs font-medium text-fg-muted">
                        {TYPED_COUNTRY_LABEL}
                      </p>
                      {/*
                       * ⛔ **ONE PER LINE, NEVER COMMA-JOINED, and the reason is
                       * three lines up: the Location value directly above IS a
                       * comma-joined `city, country`.** Two typed countries
                       * rendered the same way would read as one place — the exact
                       * misreading this whole block exists to prevent, produced
                       * by a separator.
                       */}
                      {typedCountryNames.map((typedName) => (
                        <p key={typedName} className="mt-0.5 break-words text-fg">
                          {typedName}
                        </p>
                      ))}
                      <p className="mt-1 text-xs text-fg-muted">
                        {typedCountryNames.length > 1
                          ? TYPED_COUNTRY_DISAGREEMENT
                          : TYPED_COUNTRY_NOTE}
                      </p>
                    </div>
                  )}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-xs font-medium text-fg-muted">
                  This link expires
                </dt>
                {/*
                 * A real deadline, from the server's own `expiresAt` — never
                 * computed here from a duration. The 410 screen is what somebody
                 * gets after it passes, and a date they can see beforehand is the
                 * difference between acting today and discovering it tomorrow.
                 */}
                <dd className="mt-0.5 text-sm break-words text-fg">
                  {formatDateTime(expiresAt)}
                </dd>
              </div>
            </dl>
          </PanelBody>
        </Panel>

        {/*
         * ⚠️ Rendered ONLY when there is something to say. An empty list must
         * produce no block at all — "0 possible duplicates found" reads as a
         * clean bill of health the check never actually earned, and false
         * reassurance is worth less than nothing on the one screen where
         * somebody is deciding whether an account already exists.
         */}
        {matches.length > 0 && (
          <Panel className="border-warning-border">
            <PanelHeader
              title={
                <span className="flex items-center gap-2 text-warning">
                  <AlertIcon className="h-4 w-4 shrink-0" />
                  {matches.length === 1
                    ? 'Looks similar to 1 school already in SEED EMERGE'
                    : `Looks similar to ${matches.length} schools already in SEED EMERGE`}
                </span>
              }
            />
            <PanelBody>
              <p className="text-sm text-fg-secondary">{SIMILAR_NOT_DUPLICATE}</p>
              <ul className="mt-3 space-y-2">
                {/*
                 * ⚠️ Keyed by NAME AND INDEX because these rows carry NO `id`.
                 * The snapshot is four fields — name, shortName, stage, score —
                 * and is NOT the `SimilarCompany` that `GET /companies/similar`
                 * returns to a signed-in user. That one has an `id` and a
                 * server-authored `reason`; this page had been written against
                 * it, and a guard requiring those two dropped every row — a
                 * duplicate warning failing in the reassuring direction.
                 */}
                {matches.map((row, index) => (
                  <li
                    key={`${row.name}-${index}`}
                    className="rounded-md border border-border bg-surface-sunken px-3 py-2"
                  >
                    <p className="text-sm font-semibold break-words text-fg">{row.name}</p>
                    {/*
                     * Both names when they differ (owner 2026-10-27): a row may
                     * be similar on either one.
                     */}
                    {secondLineBillingName(row) !== null && (
                      <p className="mt-0.5 text-xs break-words text-fg-secondary">
                        {BILLING_NAME_LABEL}: {secondLineBillingName(row)}
                      </p>
                    )}
                    {/*
                     * Short name and stage, as facts about the existing account —
                     * the two things that help somebody recognise it. `score` is
                     * deliberately NOT printed, and no sentence is invented in
                     * its place: a number invites the reader to trust a fuzzy
                     * string comparison over their own knowledge of the account,
                     * and a client-written "why" would describe a comparison this
                     * page never performed.
                     */}
                    {(row.shortName || row.stage) && (
                      <p className="mt-0.5 text-xs break-words text-fg-muted">
                        {[row.shortName, row.stage].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </PanelBody>
          </Panel>
        )}

        {/*
         * ⚠️ Placed ABOVE the decision panel, not beside the buttons, because
         * the order on a phone IS the sequence: what the school is, what it
         * might duplicate, who is deciding, then the decision. An identity
         * control tucked in next to Approve reads as one more field to fill in
         * rather than as the step that has to happen first.
         *
         * Disabled while a decision is in flight — the name has already gone
         * out with the request, so changing it here would show one name against
         * a decision recorded under another.
         */}
        {/*
          * ⚠️ **THREE STATES, AND THE MIDDLE ONE IS NEW.** Nobody is in the
          * group at all (`NoApprovers`); somebody is, but it is not this reader
          * (`NotAnApprover`); or it is. They are ordered widest-cause-first, so
          * a reader is never told "you are not an approver" on a request that
          * nobody could have approved anyway — which would send them to ask for
          * a group membership that would not have helped.
          */}
        {!canDecide && <NoApprovers />}

        {canDecide && sessionApprover === null && (
          <NotAnApprover signedInAs={user?.name?.trim() || user?.email || 'this account'} />
        )}

        {canDecide && sessionApprover !== null && (
          <ApproverField
            approver={sessionApprover}
            index={approvers.indexOf(sessionApprover)}
            error={statedApproverError}
          />
        )}

        {/*
         * ⚠️ The whole form goes with the identity step, never one without the
         * other. The four sales defaults are only ever written BY an approval, so
         * on a request nobody can approve they are controls over a write that
         * cannot happen — and a filled-in form with no way to submit it reads as a
         * broken page rather than as a blocked request.
         */}
        {canDecide && (
        <Panel>
          <PanelHeader title="Xero sales defaults" />
          <PanelBody>
            <form onSubmit={handleApprove} noValidate>
              <div className="grid gap-4 sm:grid-cols-2">
                {/*
                 * The Billing name the Xero Contact is created under, correctable
                 * here before approving (owner 2026-10-27). Required: a blank is
                 * sent and refused by the server at this field, as is a name
                 * another company holds. `undefined` = an older API: no box.
                 */}
                {approvalBillingName !== undefined && (
                  <div className="min-w-0 sm:col-span-2">
                    <Field label={`${BILLING_NAME_LABEL} (Xero)`} error={markedFields.billingName}>
                      {({ id, 'aria-describedby': describedBy }) => (
                        <Input
                          id={id}
                          value={billingNameDraft}
                          onChange={(event) => {
                            touched.current.billingName = true;
                            setBillingNameDraft(event.target.value);
                          }}
                          maxLength={255}
                          required
                          aria-required="true"
                          autoComplete="off"
                          invalid={Boolean(markedFields.billingName)}
                          aria-describedby={describedBy}
                        />
                      )}
                    </Field>
                  </div>
                )}
                {/*
                 * ⚠️ NO "use organisation settings" row on this one. The server
                 * takes a bare required enum here while the other three are
                 * nullable — there is no organisation fallback for a line-amount
                 * type, so offering one would be an option every submission
                 * refuses. `EXCLUSIVE` is the owner's ruling and the initial
                 * value, chosen on the form by a person looking at the choice.
                 */}
                <Field label="Line amounts" error={markedFields.xeroSalesLineAmountType}>
                  {({ id, 'aria-describedby': describedBy }) => (
                    <Select
                      id={id}
                      value={lineAmountType}
                      onChange={(event) =>
                        setLineAmountType(event.target.value as XeroLineAmountType)
                      }
                      invalid={Boolean(markedFields.xeroSalesLineAmountType)}
                      aria-describedby={describedBy}
                    >
                      {/*
                       * The COMPANY enum — INCLUSIVE | EXCLUSIVE | NONE. A
                       * document's third member is `NOTAX`; the two are separate
                       * types precisely so a confused call site fails at `tsc`.
                       */}
                      {LINE_AMOUNT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>

                {/*
                 * ⚠️ THE VALUE IS A MASTER ROW ID, NOT A CODE, so there is no
                 * text fallback here and there must not be one: nobody can type
                 * a row id, and a code is not what the write takes. When the list
                 * cannot be read the control is DISABLED and the currency is left
                 * to Xero's organisation settings — an outcome somebody can see,
                 * rather than a box whose every value is refused.
                 *
                 * The options are already the intersection of our active master
                 * and the organisation's ENABLED currencies (25 of 164). Offering
                 * anything else offers choices the write refuses.
                 */}
                <Field label="Currency" error={markedFields.currencyId}>
                  {({ id, 'aria-describedby': describedBy }) => (
                    <Select
                      id={id}
                      value={currencyId}
                      onChange={(event) => {
                        touched.current.currencyId = true;
                        setCurrencyId(event.target.value);
                      }}
                      disabled={!hasOptions(currencies)}
                      invalid={Boolean(markedFields.currencyId)}
                      aria-describedby={describedBy}
                    >
                      <option value="">{USE_ORG_SETTINGS}</option>
                      {currencies.status === 'ok' &&
                        currencies.options.map((option) => (
                          <option key={option.id} value={String(option.id)}>
                            {option.code} — {option.name}
                          </option>
                        ))}
                    </Select>
                  )}
                </Field>

                {/*
                 * ⚠️ The one control that KEEPS a typed fallback, and the reason
                 * is asymmetric with the letterhead below.
                 *
                 * A tax code this organisation does not have is REFUSED by the
                 * companies service, which validates it against a live read of
                 * the tenant's rates — so a typed value fails visibly, at once,
                 * with a 422 naming this field. And this is the field whose
                 * absence blocks every quote push (`XERO_NO_TAX_TYPE`), so
                 * disabling it when Xero is unreadable would mean approving a
                 * company nobody can quote — exactly what the approval step
                 * exists to prevent.
                 */}
                <Field label="Tax rate" error={markedFields.xeroSalesTaxType}>
                  {({ id, 'aria-describedby': describedBy }) =>
                    hasOptions(taxRates) ? (
                      <Select
                        id={id}
                        value={taxType}
                        onChange={(event) => {
                          touched.current.taxType = true;
                          setTaxType(event.target.value);
                        }}
                        invalid={Boolean(markedFields.xeroSalesTaxType)}
                        aria-describedby={describedBy}
                      >
                        <option value="">{USE_ORG_SETTINGS}</option>
                        {taxRates.status === 'ok' &&
                          taxRates.options.map((option) => (
                            <option key={option.taxType} value={option.taxType}>
                              {option.name}
                            </option>
                          ))}
                      </Select>
                    ) : (
                      <Input
                        id={id}
                        value={taxType}
                        /*
                         * ⚠️ The tax CODE alone. The server derives the
                         * percentage from Xero's own `EffectiveRate`; a client
                         * that looked a rate up and sent it would be a second
                         * writer of a number the server owns.
                         */
                        onChange={(event) => {
                          touched.current.taxType = true;
                          setTaxType(event.target.value);
                        }}
                        autoCorrect="off"
                        spellCheck={false}
                        invalid={Boolean(markedFields.xeroSalesTaxType)}
                        aria-describedby={describedBy}
                      />
                    )
                  }
                </Field>

                {/*
                 * ⚠️ NO typed fallback, deliberately, and this is the opposite
                 * call to the tax code above.
                 *
                 * A branding theme is FORMAT-CHECKED ONLY on a company write —
                 * the server does not resolve it against a live Xero read,
                 * because doing so would put a third-party round trip inside an
                 * approval. So a wrong GUID is STORED, forwarded to Xero
                 * unexamined, and surfaces months later as a document with no
                 * letterhead. `null` here is a real, correct answer meaning "use
                 * the organisation default", so withholding the control costs
                 * nothing and typing into it costs something silent.
                 *
                 * The list arrives in Xero's own order with a null `sortOrder`
                 * sorted LAST — never re-sort it here.
                 */}
                <Field label="Branding theme" error={markedFields.xeroBrandingThemeId}>
                  {({ id, 'aria-describedby': describedBy }) => (
                    <Select
                      id={id}
                      value={brandingThemeId}
                      onChange={(event) => {
                        touched.current.brandingThemeId = true;
                        setBrandingThemeId(event.target.value);
                      }}
                      disabled={!hasOptions(brandingThemes)}
                      invalid={Boolean(markedFields.xeroBrandingThemeId)}
                      aria-describedby={describedBy}
                    >
                      <option value="">{USE_ORG_SETTINGS}</option>
                      {brandingThemes.status === 'ok' &&
                        brandingThemes.options.map((option) => (
                          <option key={option.brandingThemeId} value={option.brandingThemeId}>
                            {option.name}
                          </option>
                        ))}
                    </Select>
                  )}
                </Field>
              </div>

              {approveFailure.banner.length > 0 && (
                <Notice tone="danger" role="alert" className="mt-4 break-words">
                  {approveFailure.banner.map((message) => (
                    <span key={message} className="mt-1 block first:mt-0">
                      {message}
                    </span>
                  ))}
                </Notice>
              )}

              {/*
               * Stacked below `sm` so both actions keep a full-width 44px target
               * on a phone, which is where an emailed link is usually opened.
               * Reversed above it so Approve sits on the right as the primary.
               */}
              <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse sm:justify-start">
                <Button
                  type="submit"
                  variant="primary"
                  loading={submitting === 'approve'}
                  disabled={submitting !== null || identityMissing}
                  className="w-full sm:w-auto"
                >
                  Approve
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => {
                    setSubmitError(null);
                    setRejectOpen(true);
                  }}
                  disabled={submitting !== null || identityMissing}
                  className="w-full sm:w-auto"
                >
                  Reject
                </Button>
              </div>

              {/*
               * ⚠️ **Why the two buttons are inert — the house rule's own
               * exception, not helper text.** A bare `disabled` makes "you have
               * not said who you are yet" look identical to "this page is
               * broken", and this reader has one link, no account and nobody to
               * ask. It renders BELOW the buttons rather than above them so it
               * appears where the reader is looking when they press one, and it
               * disappears the moment a name is chosen — a permanent sentence
               * would be advice, which is exactly what the rule prohibits.
               */}
              {decisionBlockedReason !== null && (
                <p className="mt-2 text-xs text-fg-muted">{decisionBlockedReason}</p>
              )}
            </form>
          </PanelBody>
        </Panel>
        )}
      </div>

      <ConfirmDialog
        open={rejectOpen}
        onClose={() => {
          // A request in flight cannot be recalled, so offering to cancel it would lie.
          if (submitting === null) setRejectOpen(false);
        }}
        onConfirm={handleReject}
        title="Reject this request"
        tone="danger"
        confirmLabel="Reject request"
        loading={submitting === 'reject'}
        error={
          rejectFailure.banner.length > 0 ? (
            <span className="space-y-1">
              {rejectFailure.banner.map((message) => (
                <span key={message} className="block">
                  {message}
                </span>
              ))}
            </span>
          ) : undefined
        }
        description={
          <div className="space-y-3">
            <p>
              {company.name} will not be added to SEED EMERGE, and this link closes once you
              confirm.
            </p>
            {/*
             * ⚠️ The stated name, restated at the point of no return — a
             * `ConfirmDialog` naming the real consequence, which is the other
             * exception the house rule lists. It says "recorded against",
             * matching the settled screen word for word, so the sentence the
             * approver agreed to is the sentence they are shown afterwards.
             * Rendered only when there is a name: on a deployment that names
             * the decider from the token there is nothing here to promise.
             */}
            {statedName !== null && <p>The rejection is recorded against {statedName}.</p>}
            <Field label="Reason">
              {({ id, 'aria-describedby': describedBy }) => (
                <Textarea
                  id={id}
                  value={rejectReason}
                  onChange={(event) => setRejectReason(event.target.value)}
                  rows={3}
                  /*
                   * ⚠️ The server's OWN ceiling, not a house limit —
                   * `rejectCompanySchema` is `.string().trim().max(1000)`. This
                   * is the one place a client-side cap is not "enforcing what
                   * the server permits": it refuses exactly what the server
                   * refuses, and it refuses it while the approver is still
                   * looking at the box. The alternative is a 422 that arrives
                   * after they have written a paragraph and pressed the button.
                   *
                   * The server trims before measuring, so trimming can only
                   * shorten — this cap can never be stricter than the schema.
                   * Move it only if that `.max()` moves.
                   */
                  maxLength={1000}
                  aria-describedby={describedBy}
                />
              )}
            </Field>
          </div>
        }
      />
    </Shell>
  );
}

/**
 * The page's own `<main>`. There is no `AppShell` on a public route, so this is
 * the only landmark on the document and the only place the canvas colour is
 * set — a transparent body would borrow whatever the browser painted.
 *
 * `max-w-3xl` rather than `AuthLayout`'s 400px column: this page carries a
 * details block, a warning list and a four-control grid, none of which is a
 * sign-in card. It stays a single column below `sm`, which is where an emailed
 * link is usually opened.
 */
function Shell({ children }: { children: ReactNode }) {
  const year = new Date().getFullYear();

  return (
    <div className="min-h-svh w-full bg-canvas text-fg">
      <main className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 sm:py-12">
        {children}
        <p className="mt-4 text-center text-xs text-fg-subtle">
          © {year} SEED Global Education. All rights reserved.
        </p>
      </main>
    </div>
  );
}

/**
 * A person, rendered defensively.
 *
 * Staff records reach this app with `name: ''` in more than one shape, and an
 * empty string in a value slot reads as a missing record rather than a bad one.
 * The email is the fallback that always identifies somebody.
 */
function personLabel(person: { name: string; email: string }): string {
  return person.name.trim() || person.email.trim() || 'someone at SEED';
}
